import React, { useState } from 'react';
import { GitHubFile } from '../services/github';

interface FileTreeProps {
  files: GitHubFile[];
  onFileSelect: (file: GitHubFile) => void;
  onFolderExpand?: (path: string) => void;
  loading?: boolean;
  expandedFolders?: Set<string>;
}

interface FileTreeNode {
  file: GitHubFile;
  children: FileTreeNode[];
  isExpanded: boolean;
}

const FileTree: React.FC<FileTreeProps> = ({
  files,
  onFileSelect,
  onFolderExpand,
  loading = false,
  expandedFolders = new Set()
}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(expandedFolders);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // 构建文件树结构
  const buildTree = (files: GitHubFile[]): FileTreeNode[] => {
    const tree: FileTreeNode[] = [];
    const pathMap = new Map<string, FileTreeNode>();

    // 首先按路径排序，确保文件夹在文件前面，按字母顺序排列
    files.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'dir' ? -1 : 1;
      }
      return a.path.localeCompare(b.path);
    });

    files.forEach(file => {
      const parts = file.path.split('/');
      let currentPath = '';
      let parentPath = '';

      for (let i = 0; i < parts.length; i++) {
        currentPath = parts.slice(0, i + 1).join('/');

        if (!pathMap.has(currentPath)) {
          const isLastPart = i === parts.length - 1;
          let nodeFile: GitHubFile;

          if (isLastPart) {
            nodeFile = file;
          } else {
            // 创建虚拟的文件夹节点
            nodeFile = {
              name: parts[i],
              path: currentPath,
              type: 'dir',
              size: 0,
            };
          }

          const node: FileTreeNode = {
            file: nodeFile,
            children: [],
            isExpanded: expandedNodes.has(currentPath),
          };

          pathMap.set(currentPath, node);

          if (parentPath && pathMap.has(parentPath)) {
            pathMap.get(parentPath)!.children.push(node);
          } else {
            tree.push(node);
          }
        } else {
          // 更新展开状态
          const existingNode = pathMap.get(currentPath)!;
          existingNode.isExpanded = expandedNodes.has(currentPath);
        }

        parentPath = currentPath;
      }
    });

    return tree;
  };

  const toggleNode = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
      // 通知父组件加载文件夹内容（如果还没有加载过）
      if (onFolderExpand) {
        onFolderExpand(path);
      }
    }
    setExpandedNodes(newExpanded);
  };

  const handleFileClick = (file: GitHubFile) => {
    console.log('=== FileTree: File Clicked ===');
    console.log('File clicked:', file.name);
    console.log('File path:', file.path);
    console.log('File type:', file.type);

    if (file.type === 'dir') {
      console.log('Directory clicked, toggling expansion');
      toggleNode(file.path);
    } else {
      console.log('File clicked, calling onFileSelect');
      setSelectedFile(file.path);
      onFileSelect(file);
      console.log('onFileSelect called');
    }
  };

  const getFileIcon = (file: GitHubFile, isExpanded: boolean = false) => {
    if (file.type === 'dir') {
      return (
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      );
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, React.ReactElement> = {
      js: <span className="text-yellow-400">{'</>'}</span>,
      ts: <span className="text-blue-400">{'</>'}</span>,
      jsx: <span className="text-yellow-400">{'</>'}</span>,
      tsx: <span className="text-blue-400">{'</>'}</span>,
      py: <span className="text-green-400">🐍</span>,
      java: <span className="text-red-400">☕</span>,
      cpp: <span className="text-blue-500">{'</>'}</span>,
      c: <span className="text-gray-400">{'</>'}</span>,
      html: <span className="text-orange-500">{'</>'}</span>,
      css: <span className="text-purple-400">{'</>'}</span>,
      json: <span className="text-gray-300">{'{ }'}</span>,
      md: <span className="text-blue-300">📝</span>,
    };

    return iconMap[ext || ''] || <span className="text-gray-400">📄</span>;
  };

  const renderTree = (nodes: FileTreeNode[], level = 0) => {
    return nodes.map(node => (
      <div key={node.file.path}>
        <div
          className={`flex items-center space-x-1 px-2 py-1 cursor-pointer hover:bg-gray-700 rounded ${
            selectedFile === node.file.path ? 'bg-gray-700' : ''
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => handleFileClick(node.file)}
        >
          {getFileIcon(node.file, node.isExpanded)}
          <span className={`text-sm ${
            node.file.type === 'dir' ? 'text-gray-300' : 'text-gray-400'
          } ${selectedFile === node.file.path ? 'text-white' : ''}`}>
            {node.file.name}
          </span>
          {node.file.type === 'dir' && node.children.length > 0 && (
            <span className="text-xs text-gray-500 ml-auto">
              ({node.children.length})
            </span>
          )}
        </div>
        {node.file.type === 'dir' &&
         node.children.length > 0 &&
         node.isExpanded && (
          <div>
            {renderTree(node.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-400">Loading files...</div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-400">No files found</div>
      </div>
    );
  }

  const tree = buildTree(files);

  return (
    <div className="h-full overflow-y-auto bg-gray-800">
      <div className="p-2">
        <div className="text-xs text-gray-500 font-mono mb-2">EXPLORER</div>
        {renderTree(tree)}
      </div>
    </div>
  );
};

export default FileTree;